import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext.js';
import { getApiClient } from '../../api/apiClient';
import { 
  MagnifyingGlassIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import AutoShopStats from '../autoshop/AutoShopStats.jsx';
import AutoShopTable from '../autoshop/AutoShopTable.jsx';
import AutoShopDetailsModal from '../autoshop/AutoShopDetailsModal.jsx';
import AddAutoShopModal from '../autoshop/AddAutoShopModal.jsx';

const AutoShop = () => {
  const { t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedShopDetails, setSelectedShopDetails] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Fetch clients (now auto shops)
  const fetchClients = async () => {
    try {
      setLoading(true);
      const client = getApiClient();
      const response = await client.get('/admin/shop/all');
      console.log('Fetched auto shops:', response.data);
      
      // Transform API response to match our component structure
      const transformedClients = response.data.map(shop => ({
        id: shop.shopId,
        name: shop.brandName,
        email: shop.shopEmail,
        phone: shop.phone,
        address: shop.businessAddress || '',
        company: shop.businessName || shop.brandName,
        customerSubtype: 'BUSINESS_ENTITY',
        taxIdentifier: shop.businessTaxIdentifier || '',
        regCom: shop.regCom || '',
        city: shop.businessCity || '',
        countryCode: shop.countryCodeIsoA2 || 'RO',
        subdivisionCode: shop.subdivisionCodeIsoA2 || '',
        vatPayer: shop.vatPayer || false,
        createdAt: shop.shopCreatedAt,
        updatedAt: shop.shopUpdatedAt,
        role: shop.role,
        permissions: shop.permissions,
        firebaseUid: shop.firebaseUid,
        hasMfinanteTokens: shop.hasMfinanteTokens,
        tokensValid: shop.tokensValid,
        tokensExpireAt: shop.tokensExpireAt,
        refreshTokensExpireAt: shop.refreshTokensExpireAt,
        tokensLastQueried: shop.tokensLastQueried,
        tokensCreatedAt: shop.tokensCreatedAt,
        tokensUpdatedAt: shop.tokensUpdatedAt,
        refreshTokensValid: shop.refreshTokensValid,
        hasAutopassCredentials: shop.hasAutopassCredentials,
        autopassTokenValid: shop.autopassTokenValid,
        autopassId: shop.autopassId,
        autopassEmail: shop.autopassEmail,
        autopassAgentId: shop.autopassAgentId,
        autopassUserId: shop.autopassUserId,
        autopassTokenExpiration: shop.autopassTokenExpiration,
        bankName: shop.bankName,
        iban: shop.iban
      }));
      setClients(transformedClients);
    } catch (error) {
      console.error('Error fetching auto shops:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Handle view details
  const handleViewDetails = (shop) => {
    setSelectedShopDetails(shop);
    setShowDetailsModal(true);
  };

  // Handle role change
  const handleRoleChange = async (shopId, newRole, newPermissions) => {
    try {
      const client = getApiClient();
      await client.post('/admin/shop/change-role', {
        shopId: shopId,
        role: newRole,
        permissions: newPermissions
      });
      
      alert('Role and permissions updated successfully!');
      // Refresh the list to show updated data
      fetchClients();
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Failed to update role and permissions: ' + (error.response?.data?.message || error.message));
    }
  };

  // Handle edit client
  const handleEdit = () => {
    alert('Edit functionality will be implemented later');
  };

  // Handle delete client
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm(t('confirmDeleteClient'))) return;
    try {
      alert('Delete functionality will be implemented when API provides delete endpoint');
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  // Filter clients based on search term
  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.phone?.includes(searchTerm) ||
    client.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.role?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
    client.permissions?.toLowerCase()?.includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6 sm:px-6 md:px-8">
      <div className="space-y-6">

        <AutoShopStats clients={clients} />

        {/* Header with Add Button and Search */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Search */}
          <div className="max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('searchAutoShops')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>

          {/* Add New Shop Button */}
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('addNewAutoShop')}
          </button>
        </div>

        <AutoShopTable 
          clients={filteredClients} 
          onEdit={handleEdit}
          onDelete={handleDeleteClient}
          onRoleChange={handleRoleChange}
          onViewDetails={handleViewDetails}
        />

        {/* Details Modal */}
        <AutoShopDetailsModal 
          shop={selectedShopDetails}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedShopDetails(null);
          }}
          onShopUpdate={fetchClients}
        />

        {/* Add Shop Modal */}
        <AddAutoShopModal 
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={fetchClients}
        />
      </div>
    </div>
  );
};

export default AutoShop;
